import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { IVerbaScholaPinyinReview } from '../api/VerbaScholaLessonAiApi';
import { WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';
import { instructionalPinyin } from './studentInstructionalPinyin';

function ReferenceSyllable({ syllable, reference, character }: { syllable: string; reference: string; character: string }) {
    const [open, setOpen] = React.useState(false);
    return <Tooltip open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} enterTouchDelay={0}
        title={`Reference for ${character}: ${instructionalPinyin([reference])}. Check the meaning in context.`}>
        <Box component="button" type="button" onClick={() => setOpen(true)} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
            aria-label={`${instructionalPinyin([syllable])} differs from reference ${instructionalPinyin([reference])} for ${character}`}
            sx={{ bgcolor: WARNING_SOFT, color: WARNING_TEXT, border: 0, borderBottom: '1px dotted currentColor', borderRadius: 0.5,
                px: 0.25, py: 0, font: 'inherit', lineHeight: 'inherit', cursor: 'help' }}>
            {instructionalPinyin([syllable])}
        </Box>
    </Tooltip>;
}

/** Advisory dictionary differences; never a pronunciation verdict or correction. */
export function PinyinReview({ review, expanded = false }: { review?: IVerbaScholaPinyinReview | null; expanded?: boolean }) {
    if (!review) return null;
    return <Box component="details" open={expanded} aria-label="Pronunciation reference comparison" sx={{ my: 1, overflowWrap: 'anywhere' }}>
        <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
            Pronunciation review · {review.differences.length} {review.differences.length === 1 ? 'pair differs' : 'pairs differ'}
        </Box>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ my: 1 }}>
            Highlighted syllables differ from a dictionary reference with reviewed whole-word readings.
            The reference can be wrong in context. Matching readings are not verified; check all pronunciation before marking ready.
            Values are unchanged. Plan and material prose are not compared.
        </Typography>
        {review.unavailable_pairs > 0 && <Typography variant="body2">Reference unavailable for {review.unavailable_pairs} {review.unavailable_pairs === 1 ? 'pair' : 'pairs'}.</Typography>}
        {review.differences.length === 0 && <Typography variant="body2">No reference differences in {review.compared_pairs} compared {review.compared_pairs === 1 ? 'pair' : 'pairs'}.</Typography>}
        {review.differences.map((pair, index) => {
            const characters = Array.from(pair.text).filter(character => /[\u4e00-\u9fff]/.test(character));
            return <Box key={index} sx={{ my: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{pair.label}</Typography>
                <Typography variant="body2">{pair.text}</Typography>
                <Typography variant="body2" component="div" aria-label={`Draft pinyin for ${pair.text}`}>
                    {pair.pinyin.map((syllable, syllableIndex) => <Box component="span" key={syllableIndex}>
                        {syllableIndex > 0 ? ' ' : ''}
                        {pair.differing_indices.includes(syllableIndex) ? <ReferenceSyllable syllable={syllable}
                            reference={pair.reference_pinyin[syllableIndex]} character={characters[syllableIndex]} /> : instructionalPinyin([syllable])}
                    </Box>)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Reference: {instructionalPinyin(pair.reference_pinyin)}</Typography>
            </Box>;
        })}
    </Box>;
}
